import { trackDistanceMeters, trackStats } from './gpsTrack';

// Symbolic + word operator tokens → canonical op code. The generator commonly emits
// symbolic operators ('>=') for where()/filters, so accept both.
const WHERE_OPS: Record<string, string> = {
  '==': 'eq', '=': 'eq', eq: 'eq',
  '!=': 'ne', '<>': 'ne', ne: 'ne',
  '>': 'gt', gt: 'gt',
  '<': 'lt', lt: 'lt',
  '>=': 'gte', gte: 'gte',
  '<=': 'lte', lte: 'lte',
};

/** Resolve where()'s trailing args to {value, op}, tolerating BOTH argument orders:
 *  the canonical where(arr, field, value, opCode) AND the form the generator commonly
 *  emits — where(arr, field, OP, value) with a symbolic operator like '>='. The 3rd arg
 *  is read as the operator ONLY when it's a recognized operator token AND a 4th arg is
 *  present, so where(arr, 'code', '>=') still filters for the literal value '>='. */
function normalizeWhereArgs(a: any, b: any): { value: any; op: string } {
  if (b !== undefined && typeof a === 'string' && WHERE_OPS[a.trim()]) {
    return { value: b, op: WHERE_OPS[a.trim()] };
  }
  // Single 3rd arg that GLUES a comparison operator onto the value — e.g.
  // where(data, 'snowDepth', '> 0') or where(data, 'price', '<=50'). The generator emits
  // this; split it into op + value. Restricted to the ordering/inequality operators (not
  // bare '='), and only when there's no 4th arg, so a literal like where(data,'code','>=')
  // (handled above with a 4th arg) and plain strings stay intact.
  if (b === undefined && typeof a === 'string') {
    const m = /^(>=|<=|!=|<>|>|<)\s*(.+)$/.exec(a.trim());
    if (m) {
      const raw = m[2].trim();
      const num = Number(raw);
      return { value: raw !== '' && !isNaN(num) ? num : raw, op: WHERE_OPS[m[1]] };
    }
  }
  return { value: a, op: typeof b === 'string' && WHERE_OPS[b.trim()] ? WHERE_OPS[b.trim()] : 'eq' };
}

const looksLikeDate = (s: any): boolean => typeof s === 'string' && /[-/T:]/.test(s) && !isNaN(Date.parse(s));

/** Compare a row's field value against `value` under `op`, with date/number awareness for
 *  ordering ops so "date >= <iso>" and numeric-string compares work. eq/ne stay loose (==). */
function compareWhere(v: any, value: any, op: string): boolean {
  if (op === 'eq') return v == value;
  if (op === 'ne') return v != value;
  let l: any = v, r: any = value;
  if (looksLikeDate(v) && looksLikeDate(value)) { l = Date.parse(v); r = Date.parse(value); }
  switch (op) {
    case 'gt': return l > r;
    case 'lt': return l < r;
    case 'gte': return l >= r;
    case 'lte': return l <= r;
    default: return v == value;
  }
}

const builtinFunctions: Record<string, (...args: any[]) => any> = {
  count(arr: any[]): number {
    if (!Array.isArray(arr)) return 0;
    return arr.length;
  },

  sum(arr: any[], field?: string): number {
    if (!Array.isArray(arr)) return 0;
    return arr.reduce((acc: number, item: any) => {
      const val = field ? item?.[field] : item;
      return acc + (typeof val === 'number' ? val : 0);
    }, 0);
  },

  avg(arr: any[], field?: string): number {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    const total = builtinFunctions.sum(arr, field);
    return total / arr.length;
  },

  min(...args: any[]): number {
    const nums = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
    if (nums.length === 0) return 0; // empty → 0, not Infinity (breaks dashboards)
    return Math.min(...nums);
  },

  max(...args: any[]): number {
    const nums = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
    if (nums.length === 0) return 0; // empty → 0, not -Infinity
    return Math.max(...nums);
  },

  // Return a NEW array sorted by `field` (asc default). Non-mutating.
  sortBy(arr: any[], field?: string, dir: 'asc' | 'desc' = 'asc'): any[] {
    if (!Array.isArray(arr)) return [];
    const sign = dir === 'desc' ? -1 : 1;
    return [...arr].sort((a, b) => {
      const av = field ? a?.[field] : a;
      const bv = field ? b?.[field] : b;
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      return 0;
    });
  },

  // Filter rows where `field` compares to `value`. Tolerates BOTH arg orders —
  // where(arr, field, value, opCode) and where(arr, field, OP, value) — and symbolic
  // operators ('>='), because the generator emits either. Date/number-aware ordering,
  // loose equality on eq/ne so string/number mixes from expressions still match.
  where(arr: any[], field: string, a: any, b?: any): any[] {
    if (!Array.isArray(arr)) return [];
    const { value, op } = normalizeWhereArgs(a, b);
    return arr.filter((item: any) => compareWhere(item?.[field], value, op));
  },

  filterBy(arr: any[], field: string, a: any, b?: any): any[] {
    return builtinFunctions.where(arr, field, a, b);
  },

  // Group rows into { fieldValue: rows[] }.
  groupBy(arr: any[], field: string): Record<string, any[]> {
    if (!Array.isArray(arr)) return {};
    const out: Record<string, any[]> = {};
    for (const item of arr) {
      const key = String(item?.[field]);
      if (!out[key]) out[key] = [];
      out[key].push(item);
    }
    return out;
  },

  take(arr: any[], n: number): any[] {
    return Array.isArray(arr) ? arr.slice(0, n) : [];
  },

  first(arr: any[]): any {
    return Array.isArray(arr) ? arr[0] : undefined;
  },

  last(arr: any[]): any {
    return Array.isArray(arr) ? arr[arr.length - 1] : undefined;
  },

  // Array of the values of `field` across rows.
  pluck(arr: any[], field: string): any[] {
    return Array.isArray(arr) ? arr.map((i: any) => i?.[field]) : [];
  },

  unique(arr: any[]): any[] {
    return Array.isArray(arr) ? [...new Set(arr)] : [];
  },

  round(num: number, decimals: number = 0): number {
    if (typeof num !== 'number' || isNaN(num)) return 0;
    const f = Math.pow(10, decimals);
    return Math.round(num * f) / f;
  },

  formatDate(date: string, format?: string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    if (!format) {
      return d.toLocaleDateString();
    }

    // Simple format replacements
    let result = format;
    result = result.replace('YYYY', String(d.getFullYear()));
    result = result.replace('MM', String(d.getMonth() + 1).padStart(2, '0'));
    result = result.replace('DD', String(d.getDate()).padStart(2, '0'));
    result = result.replace('HH', String(d.getHours()).padStart(2, '0'));
    result = result.replace('mm', String(d.getMinutes()).padStart(2, '0'));
    result = result.replace('ss', String(d.getSeconds()).padStart(2, '0'));
    return result;
  },

  formatNumber(num: number, decimals?: number): string {
    if (typeof num !== 'number' || isNaN(num)) return '';
    if (decimals !== undefined) {
      return num.toFixed(decimals);
    }
    return String(num);
  },

  upper(str: string): string {
    if (typeof str !== 'string') return '';
    return str.toUpperCase();
  },

  lower(str: string): string {
    if (typeof str !== 'string') return '';
    return str.toLowerCase();
  },

  contains(str: string, substr: string): boolean {
    if (typeof str !== 'string' || typeof substr !== 'string') return false;
    return str.includes(substr);
  },

  length(val: any[] | string): number {
    if (val == null) return 0;
    if (typeof val === 'string' || Array.isArray(val)) return val.length;
    return 0;
  },

  now(): string {
    return new Date().toISOString();
  },

  if(condition: any, thenVal: any, elseVal: any): any {
    return condition ? thenVal : elseVal;
  },

  default(value: any, fallback: any): any {
    return value != null ? value : fallback;
  },

  // ── Game / randomness helpers ──────────────────────────────────────────────
  random(): number {
    return Math.random();
  },

  // Integer in [min, max] inclusive. Swaps if reversed; floors non-integers.
  randomInt(min: any, max: any): number {
    let lo = Math.ceil(Number(min) || 0);
    let hi = Math.floor(Number(max) || 0);
    if (hi < lo) [lo, hi] = [hi, lo];
    return lo + Math.floor(Math.random() * (hi - lo + 1));
  },

  // Roll a die with `sides` faces (default 6) → 1..sides.
  dice(sides: any = 6): number {
    const n = Math.max(1, Math.floor(Number(sides) || 6));
    return 1 + Math.floor(Math.random() * n);
  },

  // Random element of an array (undefined if empty / not an array).
  pick(arr: any[]): any {
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return arr[Math.floor(Math.random() * arr.length)];
  },

  // New shuffled copy (Fisher–Yates). Non-mutating.
  shuffle(arr: any[]): any[] {
    if (!Array.isArray(arr)) return [];
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  },

  // range(n) → [0..n-1]; range(start, end) → [start..end-1]. For boards/grids
  // (e.g. data: "range(9)" to render a 3×3 board) and repeated UI.
  range(a: any, b?: any): number[] {
    const start = b === undefined ? 0 : Math.floor(Number(a) || 0);
    const end = b === undefined ? Math.floor(Number(a) || 0) : Math.floor(Number(b) || 0);
    const out: number[] = [];
    for (let i = start; i < end; i++) out.push(i);
    return out;
  },

  // ── Date arithmetic ────────────────────────────────────────────────────────
  // All take/return ISO strings; invalid input → '' (or 0/false for comparisons).
  addDays(date: any, n: any): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + (Number(n) || 0));
    return d.toISOString();
  },

  addHours(date: any, n: any): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    d.setHours(d.getHours() + (Number(n) || 0));
    return d.toISOString();
  },

  // Whole-unit difference a − b (positive when a is later).
  diffDays(a: any, b: any): number {
    const da = new Date(a), db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return 0;
    return Math.round((da.getTime() - db.getTime()) / 86400000);
  },

  diffHours(a: any, b: any): number {
    const da = new Date(a), db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return 0;
    return Math.round((da.getTime() - db.getTime()) / 3600000);
  },

  // 0 = Sunday … 6 = Saturday; -1 for invalid. UTC-based for deterministic results.
  dayOfWeek(date: any): number {
    const d = new Date(date);
    return isNaN(d.getTime()) ? -1 : d.getUTCDay();
  },

  isBefore(a: any, b: any): boolean {
    const da = new Date(a), db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
    return da.getTime() < db.getTime();
  },

  isAfter(a: any, b: any): boolean {
    const da = new Date(a), db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
    return da.getTime() > db.getTime();
  },

  // Truncate to the start of 'hour'|'day'|'week'(Sun)|'month'. Default 'day'.
  // UTC-based for deterministic ISO output (matches how timestamps are stored).
  startOf(date: any, unit: string = 'day'): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    switch (unit) {
      case 'hour': d.setUTCMinutes(0, 0, 0); break;
      case 'week': d.setUTCHours(0, 0, 0, 0); d.setUTCDate(d.getUTCDate() - d.getUTCDay()); break;
      case 'month': d.setUTCHours(0, 0, 0, 0); d.setUTCDate(1); break;
      case 'day':
      default: d.setUTCHours(0, 0, 0, 0); break;
    }
    return d.toISOString();
  },

  // ── Advanced math (for race prediction, progression, etc.) ──────────────────
  pow(base: any, exp: any): number {
    return Math.pow(Number(base) || 0, Number(exp) || 0);
  },
  sqrt(n: any): number {
    const x = Number(n);
    return x > 0 ? Math.sqrt(x) : 0;
  },
  // Natural log by default; pass a base for log_base(n). Non-positive → 0.
  log(n: any, base?: any): number {
    const x = Number(n);
    if (!(x > 0)) return 0;
    return base !== undefined ? Math.log(x) / Math.log(Number(base)) : Math.log(x);
  },
  exp(n: any): number {
    return Math.exp(Number(n) || 0);
  },
  abs(n: any): number {
    return Math.abs(Number(n) || 0);
  },
  floor(n: any): number {
    return Math.floor(Number(n) || 0);
  },
  ceil(n: any): number {
    return Math.ceil(Number(n) || 0);
  },
  // Constrain n to [lo, hi].
  clamp(n: any, lo: any, hi: any): number {
    return Math.min(Math.max(Number(n) || 0, Number(lo)), Number(hi));
  },

  // ── Trigonometry (GPS haversine distance, bearings) ─────────────────────────
  // Degrees → radians. Garbage → 0 (never NaN).
  radians(deg: any): number {
    return ((Number(deg) || 0) * Math.PI) / 180;
  },
  sin(n: any): number {
    return Math.sin(Number(n) || 0);
  },
  cos(n: any): number {
    return Math.cos(Number(n) || 0);
  },
  atan2(y: any, x: any): number {
    return Math.atan2(Number(y) || 0, Number(x) || 0);
  },
  // Great-circle distance between two lat/lng points (haversine). Powers radius
  // filters, e.g. a neighborhood feed:
  //   where($.data.posts, distanceMi($.user.lat,$.user.lng,$.item.lat,$.item.lng) <= 1)
  distanceKm(lat1: any, lng1: any, lat2: any, lng2: any): number {
    const toRad = (d: any) => ((Number(d) || 0) * Math.PI) / 180;
    const dLat = toRad(Number(lat2) - Number(lat1));
    const dLng = toRad(Number(lng2) - Number(lng1));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  },
  distanceMi(lat1: any, lng1: any, lat2: any, lng2: any): number {
    const toRad = (d: any) => ((Number(d) || 0) * Math.PI) / 180;
    const dLat = toRad(Number(lat2) - Number(lat1));
    const dLng = toRad(Number(lng2) - Number(lng1));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  },
  // Rows within `radiusMi` of (lat,lng) — the neighborhood-feed radius filter:
  //   near($.data.posts, $.user.lat, $.user.lng, 1)
  near(arr: any[], lat: any, lng: any, radiusMi: any, latField: any = 'lat', lngField: any = 'lng'): any[] {
    if (!Array.isArray(arr)) return [];
    const la = Number(lat), lo = Number(lng), r = Number(radiusMi);
    // No valid center/radius yet (e.g. $.location still loading) → don't filter.
    if (Number.isNaN(la) || Number.isNaN(lo) || Number.isNaN(r)) return arr;
    // Entities commonly name coordinates `latitude`/`longitude`, but callers usually pass no
    // field override so this defaults to `lat`/`lng`. Fall back to the long names so a
    // generated near($.data.X, $.location.lat, $.location.lng, r) works either way.
    const lf = String(latField), gf = String(lngField);
    return arr.filter((it: any) => {
      const ilat = it?.[lf] ?? (lf === 'lat' ? it?.latitude : undefined);
      const ilng = it?.[gf] ?? (gf === 'lng' ? it?.longitude : undefined);
      return builtinFunctions.distanceMi(la, lo, ilat, ilng) <= r;
    });
  },

  // ── Units & currency (cross-cutting: Orbit/Pacer distances, bookings, pay) ───
  kmToMi(km: any): number {
    return (Number(km) || 0) * 0.621371;
  },
  miToKm(mi: any): number {
    return (Number(mi) || 0) * 1.609344;
  },
  kgToLb(kg: any): number {
    return (Number(kg) || 0) * 2.2046226218;
  },
  lbToKg(lb: any): number {
    return (Number(lb) || 0) / 2.2046226218;
  },
  cToF(c: any): number {
    return (Number(c) || 0) * 1.8 + 32;
  },
  fToC(f: any): number {
    return ((Number(f) || 0) - 32) / 1.8;
  },
  mToFt(m: any): number {
    return (Number(m) || 0) * 3.280839895;
  },
  ftToM(ft: any): number {
    return (Number(ft) || 0) / 3.280839895;
  },
  // "$1,234.50" — manual grouping (Hermes-safe, no Intl), 2 decimals by default.
  formatMoney(amount: any, symbol: any = '$', decimals: any = 2): string {
    const n = Number(amount);
    if (!Number.isFinite(n)) return `${String(symbol)}0`;
    const d = Math.max(0, Math.floor(Number(decimals) ?? 2));
    const neg = n < 0;
    const fixed = Math.abs(n).toFixed(d);
    const [intPart, frac] = fixed.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${neg ? '-' : ''}${String(symbol)}${grouped}${frac ? '.' + frac : ''}`;
  },
  // ── GPS track helpers (Orbit) — points = [{latitude,longitude,timestamp?}] ──
  trackDistanceKm(points: any): number {
    return trackDistanceMeters(points) / 1000;
  },
  trackDistanceMi(points: any): number {
    return (trackDistanceMeters(points) / 1000) * 0.621371;
  },
  trackDurationSec(points: any): number {
    return trackStats(points).durationSeconds;
  },
  trackPaceSecPerKm(points: any): number {
    return trackStats(points).paceSecPerKm;
  },
  trackSpeedMps(points: any): number {
    return trackStats(points).speedMps;
  },

  // Format an ISO instant in a specific IANA time zone (e.g. 'America/New_York').
  // Falls back to the local-tz formatting if the zone is invalid/unsupported.
  formatDateTz(date: any, timeZone: any, options?: any): string {
    const d = new Date(String(date ?? ''));
    if (isNaN(d.getTime())) return '';
    const tz = timeZone ? String(timeZone) : undefined;
    const opts: any = options && typeof options === 'object' ? options : { dateStyle: 'medium', timeStyle: 'short' };
    try {
      return d.toLocaleString(undefined, tz ? { ...opts, timeZone: tz } : opts);
    } catch {
      return d.toLocaleString(undefined, opts);
    }
  },
};

export default builtinFunctions;
